/**
 * The gallery wire protocol (PLAN-GALLERY G3).
 *
 * Shared by the Worker (which writes these responses and enforces every rule
 * below) and the browser (which reads them), exactly as `share/protocol.ts` and
 * `profile/protocol.ts` are. One module, so the two halves cannot drift.
 *
 * ```
 * public   GET    /api/gallery/entries                    browse: q, style, sort, cursor
 * public   POST   /api/gallery/entries/:id/clone-ping     the vanity counter
 * session  POST   /api/likes/:entryId/like                thumbs up
 * session  POST   /api/likes/:entryId/unlike              take it back
 * session  GET    /api/listings                           my listings
 * session  GET    /api/listings/recipes/:recipeId         this recipe's listing, or null
 * session  POST   /api/listings/recipes/:recipeId         publish, or update the blurb
 * session  POST   /api/listings/recipes/:recipeId/refresh republish the current version
 * session  PATCH  /api/listings/recipes/:recipeId         edit the description only
 * session  DELETE /api/listings/recipes/:recipeId         unpublish
 * ```
 *
 * ## Why the public read lives under its own base path
 *
 * The same reason `/api/shared` and `/api/share` are two paths: the Worker's
 * session middleware lets a request through when its path starts with one of
 * `PUBLIC_API_PREFIXES`, so the public half has to sit under a prefix that
 * contains nothing else. `/api/gallery/` is browse and the clone ping, both of
 * which a signed-out visitor must reach. The owner's publish and unpublish live
 * at `/api/listings/*`, which that prefix deliberately does not match — and
 * which is a different word rather than `/api/gallery-something`, so that no
 * amount of `startsWith` on the public prefix can ever reach them.
 *
 * Liking is the third word, {@link LIKES_BASE_PATH}, and it is a third word for
 * exactly that rule: a like is written by a **named account** and must never be
 * reachable without a session, so it cannot live under `/api/gallery/` however
 * naturally `/api/gallery/:id/like` would read. The clone ping is the counter
 * that may be pinged by anybody; the like is the one that may not.
 *
 * ## What a listing is
 *
 * A **published version of a recipe**: the card a browse grid draws, and the
 * document behind it, captured together and served together from `/g/:entryId`.
 * {@link GalleryEntry.refreshedAt} is when that capture happened, and it is one
 * timestamp for one act — the card cannot describe a different beer from the
 * page it opens.
 *
 * Editing the recipe afterwards changes neither. The owner republishes when they
 * are ready ("Update listing"), which is the same "staleness is a feature with a
 * button" 0017 argued for, now applied to the whole listing rather than half of
 * it — see `migrations/0023_gallery_pinned.sql` for why the half-version was a
 * defect.
 *
 * A private share link is a different object with different rules: `/s/:token`
 * shows the recipe **as it stands**, so a typo fixed is a typo fixed for
 * everyone holding the link. Publishing does not change that and unpublishing
 * does not revoke it.
 */

import type { EquipmentProfileDoc, RecipeDoc } from '@beerwright/commands';

/** Where the public gallery routes live. Both are reachable with no session. */
export const GALLERY_PUBLIC_BASE_PATH = '/api/gallery';

/** Where the owner's publish/unpublish routes live. Session-guarded. */
export const LISTINGS_BASE_PATH = '/api/listings';

/**
 * Where a published listing is read, in the SPA.
 *
 * Its own prefix rather than `/s/`, because they are different acts on different
 * objects: `/s/:token` is a link somebody was sent and shows the recipe as it
 * stands; `/g/:entryId` is an entry in a public directory and shows the version
 * that was published to it. Giving them one URL was what forced one liveness
 * policy onto both, and made a card advertise numbers its own page contradicted.
 */
export const GALLERY_PAGE_PREFIX = '/g/';

/**
 * Where liking and unliking live. Session-guarded, and a word of its own.
 *
 * Not under `/api/gallery/`, which is public by prefix — see the header. A like
 * is one row keyed by the caller's own user id, so the route needs a session for
 * the same reason it needs a database: without one there is nothing to write.
 */
export const LIKES_BASE_PATH = '/api/likes';

/** Where the gallery page lives in the SPA. Works signed out. */
export const GALLERY_PAGE_PATH = '/gallery';

/**
 * How many cards one page of the grid carries.
 *
 * 24, which is a whole number of rows at one, two, three and four columns — the
 * four widths the grid actually renders at — so a page never ends in a ragged
 * half-row on the way to the next.
 */
export const GALLERY_PAGE_SIZE = 24;

/**
 * The longest a blurb may be, in characters.
 *
 * 280 (PLAN-GALLERY G3). It is the one free-text field in the whole feature and
 * it has to fit on a card beside the vitals without becoming the card, which is
 * the same argument a certain other product made for the same number.
 */
export const BLURB_MAX_LENGTH = 280;

/**
 * How the grid may be ordered. `newest` is the default and the honest one.
 *
 * The other two are popularity, counted two different ways and worth keeping
 * apart: `cloned` is how many people took a copy, which is an act with a
 * consequence; `most-liked` is how many people said they liked it, which is an
 * opinion. Both are shallow, neither decides anything, and a reader choosing
 * between them is choosing which shallow thing they want today.
 */
export const GALLERY_SORTS = ['newest', 'cloned', 'most-liked'] as const;

/** @see GALLERY_SORTS */
export type GallerySort = (typeof GALLERY_SORTS)[number];

/** Whether a string is a sort this build knows. Anything else reads as `newest`. */
export function parseGallerySort(value: unknown): GallerySort {
  if (value === 'cloned') return 'cloned';
  if (value === 'most-liked') return 'most-liked';
  return 'newest';
}

/**
 * The gallery view somebody is looking at: the filters, and which tab.
 *
 * `/gallery` reads exactly this out of its own URL (`routes/gallery.tsx`), and
 * a card carries it into `/g/:entryId` so the way back is the filtered grid a
 * brewer left rather than the whole directory (PLAN-UX-PASS E3). `undefined`
 * is "absent", never `null` — see `lib/search-params.ts` for what a `null`
 * round-trips into.
 */
export interface GalleryView {
  /** The search box, trimmed, or `undefined` for an empty one. */
  readonly q: string | undefined;
  /** A BJCP style id, or `undefined` for every style. */
  readonly style: string | undefined;
  /** The sort control, or `undefined` for the default (`newest`). */
  readonly sort: GallerySort | undefined;
  /** `mine` for the owner's listings, or `undefined` for Browse. */
  readonly tab: 'mine' | undefined;
}

/**
 * A view as one search-param value, or `undefined` when it is the default one.
 *
 * What the card's `from=` carries. A search string rather than the four keys
 * spread onto `/g/:entryId`'s own URL: the published page has state of its own
 * to grow, and "where I came from" is one fact rather than four of somebody
 * else's. Defaults are omitted, which is why a plain visit to the gallery puts
 * nothing at all on its cards' links.
 */
export function encodeGalleryView(view: Partial<GalleryView>): string | undefined {
  const params = new URLSearchParams();
  if (view.q !== undefined && view.q.trim() !== '') params.set('q', view.q.trim());
  if (view.style !== undefined && view.style !== '') params.set('style', view.style);
  if (view.sort !== undefined && view.sort !== 'newest') params.set('sort', view.sort);
  if (view.tab === 'mine') params.set('tab', 'mine');
  const search = params.toString();
  return search === '' ? undefined : search;
}

/**
 * The same string read back into a view, for the published page's way home.
 *
 * Total on `unknown`, like every parser in `lib/search-params.ts`: a `from=`
 * somebody typed, an old bookmark's, or none at all yields the default view
 * rather than throwing, and a sort this build does not know is dropped rather
 * than trusted.
 */
export function decodeGalleryView(from: unknown): GalleryView {
  const params = new URLSearchParams(
    typeof from === 'string' ? (from.startsWith('?') ? from.slice(1) : from) : '',
  );
  const q = params.get('q')?.trim();
  const style = params.get('style')?.trim();
  const sort = params.get('sort');
  return {
    q: q === undefined || q === '' ? undefined : q,
    style: style === undefined || style === '' ? undefined : style,
    sort: sort === null || sort === 'newest' || !isGallerySort(sort) ? undefined : sort,
    tab: params.get('tab') === 'mine' ? 'mine' : undefined,
  };
}

/** Whether a string is one of the three sorts, without defaulting it. */
function isGallerySort(value: string): value is GallerySort {
  return (GALLERY_SORTS as readonly string[]).includes(value);
}

/**
 * Tidy a blurb, or refuse it.
 *
 * Trimmed, internal whitespace runs collapsed to a single space, newlines
 * included — a card is one paragraph and a pasted three-line note would break
 * the grid rather than express anything. An empty result is `null` rather than
 * `''`, because "no blurb" is a state the column stores as NULL and the card
 * renders as nothing.
 *
 * Applied by the browser as it types and by the Worker before it writes, which
 * is why it lives here.
 */
export function checkBlurb(raw: unknown):
  | { readonly ok: true; readonly blurb: string | null }
  | {
      readonly ok: false;
    } {
  if (raw === null || raw === undefined) return { ok: true, blurb: null };
  if (typeof raw !== 'string') return { ok: false };
  const blurb = raw.trim().replace(/\s+/g, ' ');
  if (blurb.length === 0) return { ok: true, blurb: null };
  if (blurb.length > BLURB_MAX_LENGTH) return { ok: false };
  return { ok: true, blurb };
}

/** The vitals a card draws. Captured at publish; `null` where the engine had none. */
export interface GalleryVitals {
  readonly og: number | null;
  readonly abv: number | null;
  readonly ibu: number | null;
  readonly srm: number | null;
}

/** Who published a card. The gallery is attributed by definition, so never null. */
export interface GalleryByline {
  readonly displayName: string;
  /** `null` when there is no avatar. Cards draw initials instead. */
  readonly avatarUrl: string | null;
  /**
   * The brewer's short line about themselves, or `null`.
   *
   * Carried so `/g/:entryId` renders the same byline block as `/s/:token` —
   * they are the same component over the same recipe, and a bio that appeared
   * on one and not the other would be two different claims about one brewer.
   * Cards do not render it; there is no room beside a beer's name.
   */
  readonly bio: string | null;
  /** A bare Instagram handle, or `null`. The page builds the URL. */
  readonly instagram: string | null;
}

/** One card in the grid. */
export interface GalleryEntry {
  readonly id: string;
  /** The share link this listing points at. Still live, and still `/s/:token`. */
  readonly shareToken: string;
  readonly name: string;
  readonly styleId: string | null;
  readonly styleName: string | null;
  readonly vitals: GalleryVitals;
  readonly blurb: string | null;
  readonly byline: GalleryByline;
  /**
   * How many people have taken a copy. A vanity metric; `worker/gallery.ts` says
   * so at length, and the card's wording ("saves") is the reader's word for it.
   */
  readonly cloneCount: number;
  /** How many accounts have liked it. One per account, and always present. */
  readonly likeCount: number;
  /**
   * Whether the **caller** has liked it.
   *
   * Always present, and `false` for a signed-out reader rather than absent: the
   * grid renders one control either way, and a tri-state would mean every card
   * deciding what an `undefined` means. It is the one field on a public payload
   * that depends on who is asking, which is why the browse response is
   * `Cache-Control: private` — see `worker/gallery.ts`.
   */
  readonly likedByMe: boolean;
  /** When it was first published, **ms since epoch** (the column is seconds). */
  readonly publishedAt: number;
  /** When the card above was last re-captured, **ms since epoch**. */
  readonly refreshedAt: number;
}

/**
 * The owner's view of their own listing.
 *
 * Everything a card carries, plus the two things only the owner may see: which
 * of their recipes it is, and whether it has been hidden and why. A hidden entry
 * is absent from browse and present here — a listing that simply vanished would
 * leave a brewer with no answer.
 */
export interface OwnListing extends GalleryEntry {
  readonly recipeId: string;
  readonly hidden: boolean;
  readonly hiddenReason: string | null;
  /** Whether the share link behind it is still live. A revoked link goes dark. */
  readonly live: boolean;
}

/** `GET /api/gallery/entries`. Keyset pagination: pass `cursor` back to continue. */
export interface GalleryPageResponse {
  readonly ok: true;
  readonly entries: readonly GalleryEntry[];
  /**
   * The opaque cursor for the next page, or `null` at the end.
   *
   * Keyset rather than an offset, because a directory people publish into moves
   * under a reader's feet: with `OFFSET` a card published while somebody is on
   * page one pushes a card they have already seen onto page two. The cursor
   * names the last row of this page, so the next page starts after it whatever
   * has happened in front.
   */
  readonly cursor: string | null;
}

/**
 * `GET /api/gallery/entries/:id` — one listing and the version it published.
 *
 * `pinned` is `false` only for a listing published before pinning existed, whose
 * document is being read live as a fallback. The page says so rather than
 * promising a published version it cannot produce.
 */
export interface GalleryEntryResponse {
  readonly ok: true;
  readonly entry: GalleryEntry;
  readonly recipe: RecipeDoc;
  readonly equipment: EquipmentProfileDoc;
  readonly pinned: boolean;
}

/** `GET /api/listings`. */
export interface OwnListingsResponse {
  readonly ok: true;
  readonly listings: readonly OwnListing[];
}

/** `GET`/`POST` on one recipe's listing. `listing` is null when it has none. */
export interface OwnListingResponse {
  readonly ok: true;
  readonly listing: OwnListing | null;
}

/**
 * What a like or an unlike answers with: the state, not the change.
 *
 * Both fields are read back out of the row the write just made, in the same
 * transaction, so the client renders **the server's answer** rather than its own
 * arithmetic. That is what makes the pair safely idempotent from the browser's
 * side: liking something already liked, or unliking something never liked, is a
 * 200 carrying the state that is now true, and a card that optimistically
 * guessed +1 corrects itself against it without a refetch.
 */
export interface GalleryLikeResponse {
  readonly ok: true;
  readonly likeCount: number;
  readonly liked: boolean;
}

/** A refusal from any route here. `error` is a stable code, never prose. */
export interface GalleryErrorResponse {
  readonly ok: false;
  readonly error: string;
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** The public browse read, with its query string already built. */
export function galleryBrowsePath(query: {
  readonly q?: string;
  readonly style?: string;
  readonly sort?: GallerySort;
  readonly cursor?: string | null;
}): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q.trim() !== '') params.set('q', query.q.trim());
  if (query.style !== undefined && query.style !== '') params.set('style', query.style);
  if (query.sort !== undefined && query.sort !== 'newest') params.set('sort', query.sort);
  if (query.cursor !== undefined && query.cursor !== null) params.set('cursor', query.cursor);
  const search = params.toString();
  return `${GALLERY_PUBLIC_BASE_PATH}/entries${search === '' ? '' : `?${search}`}`;
}

/**
 * Whether the gallery is showing something older than the workspace is.
 *
 * Two timestamps, and deliberately no cleverer than that: a document's
 * `updatedAt` moves on every command, so this answers "has anything at all
 * happened to the recipe since it was published", not "would the card's six
 * numbers come out different". The second question is the one an owner would
 * rather have answered and it cannot be answered here — the vitals depend on the
 * equipment profile too, and re-deriving them client-side would be a second
 * implementation of the capture. The honest over-report is the right error:
 * pressing Update listing on an unchanged recipe costs nothing.
 *
 * One function, used by the publish dialog and by the badge on the recipe, so
 * the two can never disagree about whether a listing is stale.
 */
export function listingIsStale(
  listing: { readonly refreshedAt: number },
  recipeUpdatedAt: number,
): boolean {
  return recipeUpdatedAt > listing.refreshedAt;
}

/** One listing and the version it published. Public; hidden and revoked are 404. */
export function galleryEntryPath(entryId: string): string {
  return `${GALLERY_PUBLIC_BASE_PATH}/entries/${encodeURIComponent(entryId)}`;
}

/**
 * The SPA page a card opens, carrying the view it was opened from.
 *
 * `from` is {@link encodeGalleryView}'s string, and it rides on the card's
 * `href` because following one is a full document load out of the app shell:
 * the published page has no memory of the grid behind it, and the address is
 * the only carrier that survives a reload of that page (PLAN-UX-PASS E3).
 */
export function galleryEntryPagePath(entryId: string, from?: string | undefined): string {
  const path = `${GALLERY_PAGE_PREFIX}${encodeURIComponent(entryId)}`;
  return from === undefined || from === '' ? path : `${path}?from=${encodeURIComponent(from)}`;
}

/** The clone counter's ping. Fire-and-forget, after a clone has landed. */
export function clonePingPath(entryId: string): string {
  return `${GALLERY_PUBLIC_BASE_PATH}/entries/${encodeURIComponent(entryId)}/clone-ping`;
}

/** Thumbs up. Session-guarded; the row it writes is keyed by the caller's id. */
export function likePath(entryId: string): string {
  return `${LIKES_BASE_PATH}/${encodeURIComponent(entryId)}/like`;
}

/** Take it back. The same row, deleted — never a negative like. */
export function unlikePath(entryId: string): string {
  return `${LIKES_BASE_PATH}/${encodeURIComponent(entryId)}/unlike`;
}

/** One recipe's listing: `GET` reads it, `POST` publishes, `DELETE` unpublishes. */
export function listingPath(recipeId: string): string {
  return `${LISTINGS_BASE_PATH}/recipes/${encodeURIComponent(recipeId)}`;
}

/** Re-capture the card from the recipe as it stands now. */
export function listingRefreshPath(recipeId: string): string {
  return `${listingPath(recipeId)}/refresh`;
}
